/**
 * Stella chat panel — message list + input. Streams assistant
 * responses via `useChatStream`. Read-only when `readOnly` (admin
 * viewers).
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FormEvent,
} from "react";

import type {
  ChatMessage as ChatMessageRow,
  ContactStarzPayload,
  Design,
  DesignStreamPayload,
  Estimate,
  EstimateStreamPayload,
  MeasurePayload,
  OptionsCardPayload,
  Photo,
  RenderPayload,
} from "@starz/shared/api/types.gen";
import { ChatMessage } from "./ChatMessage";
import { ContactStarzButtons } from "./ContactStarzButtons";
import { DesignSketch } from "./DesignSketch";
import { OfferRenderButton } from "./OfferRenderButton";
import { OptionsCardGrid } from "./OptionsCardGrid";
import { PhotosPicker, type PhotosPickerPayload } from "./PhotosPicker";
import { StreamingCursor } from "./StreamingCursor";
import { useChatStream } from "@starz/shared/chat/useChatStream";
import { listEstimateMessages } from "@starz/shared/api/sdk.gen";
import {
  buildSelectedMaterialTurn,
  buildSelectedPhotosTurn,
  buildSelectedRenderPhotoTurn,
} from "@starz/shared/chat/directives";

interface Props {
  estimateId: string;
  initialMessages: ChatMessageRow[];
  readOnly?: boolean;
  /** Greeting injected when the conversation has no rows yet. */
  emptyGreeting?: string;
  /** Pickers to render on load — typically the customer's saved
   * MaterialSelection rows reconstructed as collapsed (single-card)
   * pickers anchored at the chat position where the selection was
   * made. Without this the picker history disappears on reload. */
  initialPickers?: PickerState[];
  /** Latest design fetched on workspace load so the SVG sketch
   * re-renders after a refresh (Spec 007). Null when no design has
   * been emitted yet for this estimate. */
  initialDesign?: Design | null;
  /** Callback fired when a new `event: design` SSE frame arrives.
   * Lets the workspace lift the latest design up so other panels
   * (e.g. the wall-photo overlay) stay in sync without polling. */
  onDesignUpdated?: (design: Design) => void;
  /** Phase 9 — the estimate snapshot panel moved to the workspace
   * panel. ChatPanel forwards SSE-fired snapshot updates so the
   * page can rerender the workspace card without polling. */
  onSnapshotUpdated?: (payload: EstimateStreamPayload) => void;
  /** Phase 17 — live Estimate row so chat quote cards can render
   * `current_offer` countdowns and admin offer state. */
  estimate?: Estimate | null;
  /** Phase 17 — invoked from the inline "Pay deposit" card on
   * `kind="approval"` messages. */
  onPayDeposit?: (estimateId: string) => void;
  /** Phase 17 — invoked after the customer hits "Request fresh quote"
   * on a `kind="expired_quote"` card and the refresh succeeds. */
  onRefreshed?: () => void;
  // Phase 18 — render approve / reject / Lightbox callbacks.
  onApproveRender?: (renderId: string) => void;
  onRejectRender?: (
    renderId: string,
    feedback?: {
      reason?: string;
      category?: import("@starz/shared/api/types.gen").RejectionCategory;
    },
  ) => void;
  onOpenLightbox?: (url: string, alt: string) => void;
  renderBusy?: boolean;
  /** "Continue your Project" CTA shown on an approved render. */
  onContinueProject?: () => void;
  // Phase 18 — fired after the inline [[ADD_PHOTO]] button uploads a
  // photo, so the page can refresh its photo list / quota.
  onPhotoUploaded?: (photo: Photo) => void;
  // Phase 18.4 — design sketch approve / reject from a `design_pending`
  // chat card. Wires up to the new POST /designs/{id}/approve|reject
  // endpoints; on success the page refetches messages so the card
  // collapses.
  onApproveDesign?: (designId: string) => void;
  onRejectDesign?: (designId: string) => void;
  designBusy?: boolean;
  // Phase 20 — the guided wizard shell subscribes to the live message
  // list + streaming flag so it can derive step completion and pull the
  // latest intent / material `options_payload` without owning a second
  // chat stream. Fired whenever `messages` or `streaming` changes.
  onConversationChange?: (state: { messages: ChatMessageRow[]; streaming: boolean }) => void;
}

/** Imperative handle exposed via `forwardRef` so the page-level
 * workspace panel can trigger a synthetic "ask for options" chat
 * turn without lifting the entire chat stream out of this component
 * (Phase 9). */
export interface ChatPanelHandle {
  askForOptions: () => Promise<void>;
  // Phase 18.3 — "Almost there…" CTAs from the workspace panel.
  askForEstimate: () => Promise<void>;
  askForMeasurement: () => Promise<void>;
  askForPhoto: () => Promise<void>;
  // Phase 20 — guided wizard drives the same synthetic turns the inline
  // chat fires. `selectIntent` mirrors the intent-picker tap; `sendText`
  // sends an arbitrary customer turn (e.g. the Phase 19 natural-language
  // dimensions sentence or a material `[[SELECTED:…]]` directive).
  selectIntent: (intentId: string, displayName: string) => Promise<void>;
  sendText: (text: string) => Promise<void>;
}

export interface PickerState {
  payload: OptionsCardPayload;
  /** Anchor: render this picker after the message at this index in
   * `messages`. Captured at the moment the SSE `options` event arrives
   * so the picker stays inline with Stella's reply that raised it. */
  afterMessageIndex: number;
  selectedCard: OptionsCardPayload["cards"][number] | null;
}

/** Phase 8 — Photos picker state. Same inline-anchor pattern as
 * `PickerState`. `appliedPhotoIds` is non-empty after the customer
 * taps Apply (collapses the picker; persists across reloads since the
 * photos themselves carry the anchor id). */
export interface PhotosPickerState {
  payload: PhotosPickerPayload;
  afterMessageIndex: number;
  appliedPhotoIds: string[] | null;
}

export const ChatPanel = forwardRef<ChatPanelHandle, Props>(function ChatPanel(
  {
    estimateId,
    initialMessages,
    readOnly = false,
    emptyGreeting,
    initialPickers,
    initialDesign = null,
    onDesignUpdated,
    onSnapshotUpdated,
    estimate,
    onPayDeposit,
    onRefreshed,
    onApproveRender,
    onRejectRender,
    onOpenLightbox,
    renderBusy,
    onContinueProject,
    onPhotoUploaded,
    onApproveDesign,
    onRejectDesign,
    designBusy,
    onConversationChange,
  },
  ref,
) {
  const [messages, setMessages] = useState<ChatMessageRow[]>(initialMessages);
  const [draft, setDraft] = useState("");
  const [pickers, setPickers] = useState<PickerState[]>(initialPickers ?? []);
  const [design, setDesign] = useState<Design | null>(initialDesign);
  const [photosPickers, setPhotosPickers] = useState<PhotosPickerState[]>([]);
  // Phase 17 follow-up — the SSE `measure` frame arrives mid-stream;
  // we hold the payload here and splice it onto the assistant message
  // when onTurnComplete fires so the inline measure button renders
  // immediately (not just on reload). Mirrors the mobile pattern.
  const pendingMeasureRef = useRef<MeasurePayload | null>(null);
  // Phase 18 — same race-proof pattern for the [[ADD_PHOTO]] directive:
  // hold the flag until onTurnComplete, then splice `photo_request`
  // onto the assistant message so the upload button renders inline.
  const pendingAddPhotoRef = useRef<boolean>(false);
  // Phase 18.7 — same race-proof pattern for the [[OFFER_RENDER]]
  // directive; splice `offer_render` onto the assistant message so the
  // "Render the design on my photo" button renders inline.
  const pendingOfferRenderRef = useRef<boolean>(false);
  // Phase 18 — same race-proof pattern for the [[RENDER:…]] directive.
  const pendingRenderRef = useRef<RenderPayload | null>(null);
  // Phase 18.4 race-proof — hold the streamed Design payload so
  // onTurnComplete can splice it onto the assistant message as
  // design_payload + kind="design_pending". Without this the live
  // message renders through the legacy designAfterIndex path (no
  // approve/reject buttons) and they only appear after a reload.
  const pendingDesignRef = useRef<Design | null>(null);
  // Phase 19.1 race-proof — hold the contact-starz payload from the
  // SSE frame so onTurnComplete can splice `contact_starz: true` +
  // `contact_starz_payload` onto the assistant message.
  const pendingContactStarzRef = useRef<ContactStarzPayload | null>(null);
  // Same race-proof pattern for the [[OPTIONS:…]] picker — anchor it
  // in onTurnComplete (where we know the assistant-message index for
  // sure) instead of using `messagesLengthRef.current`, which could
  // be stale if React hadn't committed the user-message append yet.
  const pendingOptionsRef = useRef<OptionsCardPayload | null>(null);
  // Anchor for the design sketch — the assistant-message index it
  // should render after. Captured at the moment the SSE `design`
  // event arrives so the sketch stays inline with Stella's reply
  // that emitted it. Reload-fallback uses the last assistant
  // message (initialDesign was raised somewhere; the last assistant
  // turn is the closest defensible position).
  const lastAssistantIndex = (() => {
    for (let i = initialMessages.length - 1; i >= 0; i--) {
      if (initialMessages[i].role === "assistant") return i;
    }
    return Math.max(0, initialMessages.length - 1);
  })();
  const [designAfterIndex, setDesignAfterIndex] = useState<number>(lastAssistantIndex);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Closure capture for messages.length inside SSE callbacks. Without
  // this, onOptions sees a stale length and pickers would all anchor
  // at the original mount-time count.
  const messagesLengthRef = useRef(messages.length);
  useEffect(() => {
    messagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Sync `initialMessages` into local state when the parent re-fetches
  // (approve / reject / refresh paths). Without this ChatPanel keeps
  // the value captured at first render and parent-driven refreshes
  // never propagate. Parent passes the same reference between renders
  // unless it deliberately replaces the array, so this fires only on
  // actual content changes.
  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);

  // Phase 18.1 — poll messages while a `render_generating` card is on
  // screen so the spinner flips to the completed image without a
  // reload. The render dispatch finishes ~10-30s after the SSE
  // `event: render` arrives, long after the chat-stream connection
  // has closed; without polling the only way to learn about the
  // kind transition is `listEstimateMessages` on demand. Re-polls
  // every 5s while at least one message is still `render_generating`;
  // stops as soon as they all flip (or the customer navigates away).
  useEffect(() => {
    const hasPending = messages.some((m) => m.kind === "render_generating");
    if (!hasPending || !estimateId) return;
    let cancelled = false;
    const id = window.setInterval(async () => {
      if (document.hidden) return;
      const res = await listEstimateMessages({ estimateId });
      if (cancelled || "error" in res) return;
      setMessages(res.data);
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [messages, estimateId]);

  const { send, streaming, currentText, error } = useChatStream({
    estimateId,
    onOptions: (payload) => {
      // Race-proof: hold the payload, anchor it in onTurnComplete when
      // we know the exact assistant-message index. The previous
      // `messagesLengthRef.current`-based anchoring could capture a
      // stale index if React hadn't committed the user-message append
      // by the time the SSE response arrived — the picker silently
      // anchored to the wrong row and disappeared until reload.
      pendingOptionsRef.current = payload as OptionsCardPayload;
    },
    onEstimate: (payload) => {
      // Phase 9 — snapshot lives in the workspace panel; lift the
      // payload to the page rather than rendering it inline.
      onSnapshotUpdated?.(payload as EstimateStreamPayload);
    },
    onDesign: (payload) => {
      const d = (payload as DesignStreamPayload).design;
      if (d) {
        setDesign(d);
        // The streaming assistant turn will append at messages.length
        // when onTurnComplete fires — anchor the sketch right after it.
        setDesignAfterIndex(messagesLengthRef.current);
        onDesignUpdated?.(d);
        // Phase 18.4 race-proof — also hold the Design so
        // onTurnComplete can splice it as design_payload + kind=
        // "design_pending". Without this the live sketch renders
        // through the legacy designAfterIndex path (no approve/reject
        // buttons) and they only appear after a reload.
        pendingDesignRef.current = d;
      }
    },
    onPhotos: (payload) => {
      const p = payload as PhotosPickerPayload;
      const afterMessageIndex = messagesLengthRef.current;
      setPhotosPickers((prev) => [
        ...prev,
        { payload: p, afterMessageIndex, appliedPhotoIds: null },
      ]);
    },
    onMeasure: (payload) => {
      // Phase 17 follow-up — hold the payload until onTurnComplete so
      // we can splice it onto the assistant message and the inline
      // measure button renders immediately (without a reload).
      pendingMeasureRef.current = payload as MeasurePayload;
    },
    onAddPhoto: () => {
      pendingAddPhotoRef.current = true;
    },
    onOfferRender: () => {
      pendingOfferRenderRef.current = true;
    },
    onContactStarz: (data) => {
      // SSE shape: { contact_starz: true, payload: ContactStarzPayload }
      const payload = (data as { payload?: ContactStarzPayload | null }).payload ?? null;
      pendingContactStarzRef.current = payload;
    },
    onRender: (payload) => {
      pendingRenderRef.current = payload as RenderPayload;
    },
    onTurnComplete: (assistantText, truncated) => {
      // After the stream completes, the server has already persisted
      // the row. We optimistically append to the local list so the
      // user doesn't see a flash; a future reload would re-fetch
      // the canonical data.
      if (assistantText) {
        const measurePayload = pendingMeasureRef.current;
        const optionsPayload = pendingOptionsRef.current;
        const photoRequest = pendingAddPhotoRef.current;
        const offerRender = pendingOfferRenderRef.current;
        const renderPayload = pendingRenderRef.current;
        const designPayload = pendingDesignRef.current;
        const contactStarzPayload = pendingContactStarzRef.current;
        pendingMeasureRef.current = null;
        pendingOptionsRef.current = null;
        pendingAddPhotoRef.current = false;
        pendingOfferRenderRef.current = false;
        pendingRenderRef.current = null;
        pendingDesignRef.current = null;
        pendingContactStarzRef.current = null;
        let appendedAtIndex = -1;
        setMessages((prev) => {
          appendedAtIndex = prev.length;
          return [
            ...prev,
            {
              estimate_id: estimateId,
              tenant_id: "",
              // `prev` already includes the user message from onSubmit;
              // the assistant sits at `prev.length` (one past the user).
              // The previous `prev.length + 1` was off by one and made
              // patchChatMessageMeasure 404 on this row.
              sequence: prev.length,
              role: "assistant",
              content: assistantText,
              input_tokens: null,
              output_tokens: null,
              model_id: null,
              truncated,
              measure_payload: measurePayload,
              options_payload: optionsPayload,
              photo_request: photoRequest,
              offer_render: offerRender,
              render_payload: renderPayload,
              design_payload: designPayload,
              contact_starz: contactStarzPayload !== null,
              contact_starz_payload: contactStarzPayload,
              // Phase 18.4 — design_pending is the kind that makes the
              // DesignSketch component render its Approve / Reject
              // button row. Render kind wins when both fire on the
              // same turn (rare but well-defined).
              kind: renderPayload
                ? "render_generating"
                : designPayload
                  ? "design_pending"
                  : undefined,
              created_at: new Date().toISOString(),
            },
          ];
        });
        // No pickers-state append for the streamed picker — renderItem
        // pulls the payload straight from `m.options_payload`, which
        // sidesteps the React-18 updater-timing race that previously
        // hid the picker until reload.
        void appendedAtIndex;
        return;
      }
      // No prose came back — drop any pending payloads to avoid them
      // leaking into the next turn.
      pendingMeasureRef.current = null;
      pendingOptionsRef.current = null;
      pendingAddPhotoRef.current = false;
      pendingOfferRenderRef.current = false;
      pendingRenderRef.current = null;
      pendingDesignRef.current = null;
      pendingContactStarzRef.current = null;
    },
  });

  // Auto-scroll to bottom on new content. Includes the visual picker
  // and structured-estimate panels — when those appear they are
  // typically the most important thing on screen, so we want them
  // visible without requiring the customer to scroll manually.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, currentText, pickers, design]);

  // Phase 20 — surface the live conversation to the guided wizard shell
  // so it can derive step completion + read the latest options payloads.
  useEffect(() => {
    onConversationChange?.({ messages, streaming });
  }, [messages, streaming, onConversationChange]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || streaming || readOnly) return;
    // Optimistically render the user's message.
    setMessages((prev) => [
      ...prev,
      {
        estimate_id: estimateId,
        tenant_id: "",
        sequence: prev.length,
        role: "user",
        content: text,
        input_tokens: null,
        output_tokens: null,
        model_id: null,
        truncated: false,
        created_at: new Date().toISOString(),
      },
    ]);
    setDraft("");
    inputRef.current?.focus();
    await send(text);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }

  async function sendAsCustomer(text: string) {
    if (streaming || readOnly) return;
    setMessages((prev) => [
      ...prev,
      {
        estimate_id: estimateId,
        tenant_id: "",
        sequence: prev.length,
        role: "user",
        content: text,
        input_tokens: null,
        output_tokens: null,
        model_id: null,
        truncated: false,
        created_at: new Date().toISOString(),
      },
    ]);
    await send(text);
  }

  async function onAskForOptions() {
    await sendAsCustomer("Show me material options for this project.");
  }
  async function onAskForEstimate() {
    await sendAsCustomer("I'm ready — give me the estimate.");
  }
  async function onAskForMeasurement() {
    await sendAsCustomer("Help me measure this space.");
  }
  async function onAskForPhoto() {
    await sendAsCustomer("I want to add a photo of my space.");
  }
  // Phase 18.7 — customer tapped the inline "Render the design on my
  // photo" button raised by Stella's [[OFFER_RENDER]] directive.
  async function onTapOfferRender() {
    await sendAsCustomer("Render the design on my photo.");
  }

  // Phase 17 — customer taps an intent kickoff card on a fresh chat.
  // Send the intent's display_name as the customer's first message;
  // Stella replies and the synthesized picker is dropped on the next
  // list fetch (real customer messages exist now).
  async function onSelectIntent(_intentId: string, displayName: string) {
    if (streaming || readOnly) return;
    const text = `I want help with my ${displayName.toLowerCase()}.`;
    setMessages((prev) => [
      ...prev,
      {
        estimate_id: estimateId,
        tenant_id: "",
        sequence: prev.length,
        role: "user",
        content: text,
        input_tokens: null,
        output_tokens: null,
        model_id: null,
        truncated: false,
        created_at: new Date().toISOString(),
      },
    ]);
    await send(text);
  }

  // Phase 9 — expose `askForOptions` to the page so the workspace
  // panel's snapshot card (which now lives in the page tree) can
  // trigger the same synthetic chat turn. No other surface needs
  // imperative access to ChatPanel today.
  useImperativeHandle(
    ref,
    () => ({
      askForOptions: () => onAskForOptions(),
      askForEstimate: () => onAskForEstimate(),
      askForMeasurement: () => onAskForMeasurement(),
      askForPhoto: () => onAskForPhoto(),
      // Phase 20 — wizard-driven turns reuse the existing customer paths.
      selectIntent: (id, name) => onSelectIntent(id, name),
      sendText: (text) => sendAsCustomer(text),
    }),
    // `onAskForOptions` reads streaming / readOnly via closure; React
    // re-creates the handle on those changes implicitly via re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [streaming, readOnly],
  );

  async function onApplyPhotos(payload: PhotosPickerPayload, photoIds: string[]) {
    if (streaming || readOnly) return;

    // Phase 21 — render-photo pick: dispatch a remodel render on the
    // chosen photo instead of linking a measurement.
    if (payload.purpose === "render") {
      const photoId = photoIds[0];
      if (!photoId) return;
      setPhotosPickers((prev) =>
        prev.map((p) =>
          p.appliedPhotoIds === null && p.payload === payload
            ? { ...p, appliedPhotoIds: [photoId] }
            : p,
        ),
      );
      const renderText = buildSelectedRenderPhotoTurn({
        photoId,
        prompt: payload.render_prompt ?? "",
      });
      setMessages((prev) => [
        ...prev,
        {
          estimate_id: estimateId,
          tenant_id: "",
          sequence: prev.length,
          role: "user",
          content: "Use this photo.",
          input_tokens: null,
          output_tokens: null,
          model_id: null,
          truncated: false,
          created_at: new Date().toISOString(),
        },
      ]);
      await send(renderText);
      return;
    }

    // Lock the picker that owns this anchor — collapse it to the
    // confirmation caption.
    setPhotosPickers((prev) =>
      prev.map((p) =>
        p.appliedPhotoIds === null && p.payload.anchor_id === payload.anchor_id
          ? { ...p, appliedPhotoIds: photoIds }
          : p,
      ),
    );
    const text = buildSelectedPhotosTurn({
      photoIds,
      anchorId: payload.anchor_id ?? "",
    });
    setMessages((prev) => [
      ...prev,
      {
        estimate_id: estimateId,
        tenant_id: "",
        sequence: prev.length,
        role: "user",
        content: "Apply this measurement to the selected photos.",
        input_tokens: null,
        output_tokens: null,
        model_id: null,
        truncated: false,
        created_at: new Date().toISOString(),
      },
    ]);
    await send(text);
  }

  async function onSelectOption(card: OptionsCardPayload["cards"][number]) {
    if (streaming || readOnly) return;
    // Lock the picker that owns this card. Other pickers (different
    // option groups raised earlier in the same chat) keep their state.
    setPickers((prev) => {
      let touched = false;
      const next = prev.map((p) => {
        if (
          p.selectedCard === null &&
          p.payload.cards.some((c) => c.option_id === card.option_id)
        ) {
          touched = true;
          return { ...p, selectedCard: card };
        }
        return p;
      });
      if (touched) return next;
      // No matching picker (streamed-picker path — rendered directly
      // from the message's options_payload). Add a synthetic entry
      // keyed by group_id so the next render finds selectedCard and
      // collapses the cards.
      if (card.option_group_id) {
        next.push({
          payload: {
            group_id: card.option_group_id,
            group_name: card.option_group_name ?? undefined,
            cards: [card],
          },
          afterMessageIndex: -1,
          selectedCard: card,
        });
      }
      return next;
    });
    // Synthetic chat turn — server intercepts the SELECTED prefix,
    // persists the selection, and replies with a deterministic ack.
    const text = buildSelectedMaterialTurn({
      optionGroupId: card.option_group_id ?? "",
      optionId: card.option_id,
      displayName: card.display_name,
    });
    setMessages((prev) => [
      ...prev,
      {
        estimate_id: estimateId,
        tenant_id: "",
        sequence: prev.length,
        role: "user",
        content: `I'll go with ${card.display_name}.`,
        input_tokens: null,
        output_tokens: null,
        model_id: null,
        truncated: false,
        created_at: new Date().toISOString(),
      },
    ]);
    await send(text);
  }

  const showEmptyState = messages.length === 0 && !currentText;

  return (
    <div className="flex h-[70vh] flex-col rounded-xl border border-starz-gold/15 bg-white shadow-sm">
      <div
        ref={scrollerRef}
        aria-live="polite"
        className="flex-1 space-y-4 overflow-y-auto px-6 py-6"
      >
        {showEmptyState && emptyGreeting && (
          <ChatMessage message={{ role: "assistant", content: emptyGreeting, truncated: false }} />
        )}
        {messages.map((m, i) => {
          const photosHere = photosPickers.filter((p) => p.afterMessageIndex === i);
          const designHere = design && designAfterIndex === i;
          // Material picker is rendered directly from the message's
          // `options_payload` — no anchor-by-index math, so it appears
          // as soon as the message is appended (no reload required).
          // `pickers` state is still consulted by group_id so the
          // picker collapses after a tap.
          const showInlineOptions = m.options_payload != null && m.kind !== "intent_picker";
          const matchingPicker = m.options_payload
            ? pickers.find((p) => p.payload.group_id === m.options_payload?.group_id)
            : null;
          const selectedCard = matchingPicker?.selectedCard ?? null;
          const pickersAtTail =
            showInlineOptions || m.kind === "intent_picker"
              ? []
              : pickers.filter((p) => p.afterMessageIndex === i);
          return (
            <div key={`${m.sequence}-${m.role}`} className="space-y-4">
              <ChatMessage
                message={m}
                estimate={estimate}
                onPayDeposit={onPayDeposit}
                onRefreshed={onRefreshed}
                onSelectIntent={(id, name) => void onSelectIntent(id, name)}
                intentPickerDisabled={streaming || readOnly}
                onApproveRender={onApproveRender}
                onRejectRender={onRejectRender}
                onOpenLightbox={onOpenLightbox}
                onContinueProject={onContinueProject}
                renderBusy={renderBusy}
                estimateId={estimateId}
                onPhotoUploaded={onPhotoUploaded}
              />
              {showInlineOptions && m.options_payload ? (
                <OptionsCardGrid
                  key={`opts-${m.sequence}`}
                  payload={
                    selectedCard
                      ? { ...m.options_payload, cards: [selectedCard] }
                      : m.options_payload
                  }
                  selectedOptionId={selectedCard?.option_id ?? null}
                  disabled={streaming || readOnly || selectedCard !== null}
                  onSelect={(card) => void onSelectOption(card)}
                />
              ) : null}
              {pickersAtTail.map((picker, idx) => (
                <OptionsCardGrid
                  key={`picker-${i}-${idx}`}
                  payload={
                    picker.selectedCard
                      ? { ...picker.payload, cards: [picker.selectedCard] }
                      : picker.payload
                  }
                  selectedOptionId={picker.selectedCard?.option_id ?? null}
                  disabled={streaming || readOnly || picker.selectedCard !== null}
                  onSelect={(card) => void onSelectOption(card)}
                />
              ))}
              {photosHere.map((picker, idx) => (
                <PhotosPicker
                  key={`photos-${i}-${idx}`}
                  payload={picker.payload}
                  appliedPhotoIds={picker.appliedPhotoIds ?? undefined}
                  disabled={streaming || readOnly || picker.appliedPhotoIds !== null}
                  onApply={(ids) => void onApplyPhotos(picker.payload, ids)}
                />
              ))}
              {/* Legacy lifted-state design — only show when the
               * message doesn't already carry its own `design_payload`,
               * otherwise we'd render the same sketch twice (and the
               * legacy one without approve/reject controls would hide
               * the new one below it). Pre-Phase-18.4 estimates still
               * surface their latest sketch via this path. */}
              {designHere && !m.design_payload && <DesignSketch design={design} />}
              {/* Phase 18.4 — design sketch pinned to this specific
               * chat message via `design_payload`. Renders with
               * approve/reject buttons when kind=design_pending; the
               * approved/dismissed chips replace the buttons after the
               * customer acts. */}
              {m.design_payload ? (
                <DesignSketch
                  design={m.design_payload}
                  kind={m.kind}
                  onApprove={
                    onApproveDesign ? () => onApproveDesign(m.design_payload!.design_id) : undefined
                  }
                  onReject={
                    onRejectDesign ? () => onRejectDesign(m.design_payload!.design_id) : undefined
                  }
                  disabled={designBusy || streaming || readOnly}
                />
              ) : null}
              {/* Phase 18.7 — inline render button when Stella emitted
               * [[OFFER_RENDER]]. One-shot: marked "done" once the
               * customer taps it so they can't double-submit. The
               * subsequent render_generating card is what fills the
               * "render in flight" UX from then on. */}
              {m.offer_render && m.role !== "user" && !readOnly ? (
                <OfferRenderButton onPress={() => void onTapOfferRender()} disabled={streaming} />
              ) : null}
              {/* Phase 19.1 — Email Starz / Call Starz buttons when
               * Stella emitted [[CONTACT_STARZ]] or the pricing
               * pipeline returned RequiresConsultation. Hidden when
               * the message has no payload (legacy / streaming
               * race). */}
              {m.contact_starz && m.contact_starz_payload && m.role !== "user" ? (
                <ContactStarzButtons payload={m.contact_starz_payload} estimateId={estimateId} />
              ) : null}
            </div>
          );
        })}
        {streaming && currentText && (
          <ChatMessage message={{ role: "assistant", content: currentText, truncated: false }} />
        )}
        {/* Pickers raised by the still-streaming turn anchor at
         * messages.length and render here until onTurnComplete moves
         * them inline with their assistant message. */}
        {pickers
          .filter((p) => p.afterMessageIndex >= messages.length)
          .map((picker, idx) => (
            <OptionsCardGrid
              key={`picker-streaming-${idx}`}
              payload={
                picker.selectedCard
                  ? { ...picker.payload, cards: [picker.selectedCard] }
                  : picker.payload
              }
              selectedOptionId={picker.selectedCard?.option_id ?? null}
              disabled={streaming || readOnly || picker.selectedCard !== null}
              onSelect={(card) => void onSelectOption(card)}
            />
          ))}
        {photosPickers
          .filter((p) => p.afterMessageIndex >= messages.length)
          .map((picker, idx) => (
            <PhotosPicker
              key={`photos-streaming-${idx}`}
              payload={picker.payload}
              appliedPhotoIds={picker.appliedPhotoIds ?? undefined}
              disabled={streaming || readOnly || picker.appliedPhotoIds !== null}
              onApply={(ids) => void onApplyPhotos(picker.payload, ids)}
            />
          ))}
        {/* Design raised by the still-streaming turn renders here
         * until onTurnComplete appends the assistant message and the
         * inline anchor takes over above. */}
        {design && designAfterIndex >= messages.length && <DesignSketch design={design} />}
        {streaming && !currentText && (
          <div className="flex items-center text-sm text-warm-gray">
            Stella is thinking
            <StreamingCursor />
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error.message}
          </div>
        )}
      </div>

      {!readOnly && (
        <form
          onSubmit={onSubmit}
          className="flex items-end gap-3 border-t border-starz-gold/15 bg-starz-off-white px-6 py-4"
        >
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={streaming}
            placeholder="Tell Stella about your project…"
            aria-label="Your message"
            rows={2}
            className="flex-1 resize-none rounded-lg border border-starz-gold/20 bg-white px-4 py-3 text-base text-starz-charcoal shadow-inner focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/40 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={streaming || !draft.trim()}
            className="min-h-[44px] min-w-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Send
          </button>
        </form>
      )}

      {readOnly && (
        <div className="border-t border-starz-gold/15 bg-starz-cream px-6 py-3 text-xs uppercase tracking-widest text-warm-gray">
          Viewing as admin — read only
        </div>
      )}
    </div>
  );
});
